/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { envUtil } from "dc-core";
import { DEFAULT_ZOOM_LEVEL } from "constants/constants";
import { getViewPortWidthWithoutPagesViewScrollbar } from "selectors/documentViewSelectors";
import { getAppContext } from "../selectors/appUiStateSelectors";
import { isIntegrationContext } from "utils/common";
import { isReviewFile } from "selectors/documentSelectors";
import { getExpandedPanelWidth } from "./SkinnyRailUtils";
import { getTruncatedZoomLevel } from "providers/previewProvider/ZoomUtilsLite";

const allowedZoomLevels = [
  0.01,
  0.0625,
  0.125,
  0.25,
  0.333,
  0.5,
  0.667,
  0.75,
  1,
  1.2,
  1.5,
  2.0,
  3.0,
  4.0,
  6.0,
  8.0,
  12.0,
  16.0
];

const getIndexOfNearestZoomLevel = zoomLevel => {
  let nearestZoomLevelIndex = 0;
  let diff = Math.abs(zoomLevel - allowedZoomLevels[0]);
  for (let i = 1; i < allowedZoomLevels.length; i++) {
    let newdiff = Math.abs(zoomLevel - allowedZoomLevels[i]);
    if (newdiff < diff) {
      diff = newdiff;
      nearestZoomLevelIndex = i;
    }
  }
  return nearestZoomLevelIndex;
};

export const getZoomInLevel = currZoomLevel => {
  let indexOfNearestZoomLevel = getIndexOfNearestZoomLevel(currZoomLevel);
  if (indexOfNearestZoomLevel !== allowedZoomLevels.length - 1) {
    return allowedZoomLevels[indexOfNearestZoomLevel + 1];
  }

  return allowedZoomLevels[allowedZoomLevels.length - 1];
};

export const getZoomOutLevel = currZoomLevel => {
  let indexOfNearestZoomLevel = getIndexOfNearestZoomLevel(currZoomLevel);
  if (indexOfNearestZoomLevel !== 0) {
    return allowedZoomLevels[indexOfNearestZoomLevel - 1];
  }
  return allowedZoomLevels[0];
};

export const getDefaultZoomLevel = stores => {
  let appContext = getAppContext(stores);
  if (isIntegrationContext(appContext)) {
    let availableWidth = getViewPortWidthWithoutPagesViewScrollbar(stores);
    if (isReviewFile(stores, envUtil)) {
      const commentsPanelWidth = getExpandedPanelWidth(stores);
      availableWidth -= commentsPanelWidth;
      return stores.zoom.getFitWidthZoomLevel(() => availableWidth);
    }
    // Trying to leave 15% of viewport from both sides.
    availableWidth *= 0.7;
    return Math.max(
      stores.zoom.getFitWidthZoomLevel(() => availableWidth),
      DEFAULT_ZOOM_LEVEL
    );
  }
  return DEFAULT_ZOOM_LEVEL;
};

export { getTruncatedZoomLevel };
